import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { organizationActions, snackbarActions } from '_state/actions'
import ChipInput from 'material-ui-chip-input'
import EmailValidator from 'email-validator'
import { FormattedMessage } from 'react-intl'
import Typography from '@material-ui/core/Typography'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Dialog from '@material-ui/core/Dialog'
import Grid from '@material-ui/core/Grid'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import { makeStyles } from '@material-ui/core'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import Input from '@material-ui/core/Input'
import MenuItem from '@material-ui/core/MenuItem'
import Checkbox from '@material-ui/core/Checkbox'
import ListItemText from '@material-ui/core/ListItemText'
import TextField from '@material-ui/core/TextField'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import { CapeeshColors } from '../assets/ColorPalette'

import { InviteRolesToDisplayName, ReverseInviteRolesToDisplayName } from '../Logic/OrganizationAssetConstants'
import { ENABLE_IDENTITY_VERIFICATION } from '../featureFlags'

const useStyles = makeStyles((theme) => ({
    chipRejected: {
        backgroundColor: '#e07676',
    },
    noEntries: {
        padding: '25px 0px',
    },
    button: {
        margin: theme.spacing(1),
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    inviteModeToggle: {
        marginBottom: theme.spacing(2),
        width: '100%',
    },
    phoneChip: {
        backgroundColor: '#e3f2fd',
    },
}))

const INVITE_MODE_EMAIL = 'email'
const INVITE_MODE_PHONE = 'phone'

const norwegianPhoneRegex = /^\+?47?\d{8}$/

const validatePhoneNumber = (phone) => {
    const cleaned = phone.replace(/[\s-]/g, '')
    return norwegianPhoneRegex.test(cleaned)
}

const normalizePhoneNumber = (phone) => {
    const cleaned = phone.replace(/[\s-]/g, '')
    if (cleaned.startsWith('+47')) return cleaned
    if (cleaned.startsWith('47') && cleaned.length === 10) return '+' + cleaned
    if (cleaned.length === 8) return '+47' + cleaned
    return cleaned
}

const EmailInvite = (props) => {
    const classes = useStyles()
    const {
        organization,
        metadata,
        inviteEmailsOpen,
        handleInviteEmailsClose,
        userGroupId,
        title,
        description,
        fixedRoles,
        availableRoles,
        callBackFunctionOnAdd,
    } = props

    const [inviteMode, setInviteMode] = useState(INVITE_MODE_EMAIL)
    const [emails, setEmails] = useState([])
    const [phoneNumbers, setPhoneNumbers] = useState([])
    const [rejectedEmails, setRejectedEmails] = useState([])
    const [rejectedPhones, setRejectedPhones] = useState([])
    const [leftoverChip, setLeftoverChip] = useState('')
    const [enableTwoFactor, setEnableTwoFactor] = useState(false)
    const [twoFactorProvider, setTwoFactorProvider] = useState('Vipps')

    const [inviteRoleDisplayName, setInviteRoleDisplayName] = useState([])
    const [inviteRolesAvailable, setInviteRolesAvailable] = useState([])

    useEffect(() => {
        if (!organization.addingStudents) {
            setEmails([])
            setPhoneNumbers([])
            setRejectedEmails([])
            setRejectedPhones([])
        }
        if (!organization) return

        if (organization.inviteRolesAvailable) {
            let inviteUserRoles = [...organization.inviteRolesAvailable]
            if (availableRoles && availableRoles.length > 0) {
                inviteUserRoles = inviteUserRoles.filter((x) => availableRoles.find((y) => y == x))
            }

            setInviteRoleDisplayName([InviteRolesToDisplayName[inviteUserRoles[0]]])
            setInviteRolesAvailable(inviteUserRoles.map((role) => InviteRolesToDisplayName[role]))
        }
    }, [])

    useEffect(() => {
        if (organization.inviteRolesAvailable) {
            let inviteUserRoles = [...organization.inviteRolesAvailable]
            if (availableRoles && availableRoles.length > 0) {
                inviteUserRoles = availableRoles.filter((x) => inviteUserRoles.find((y) => y === x))
            }

            setInviteRoleDisplayName([InviteRolesToDisplayName[inviteUserRoles[0]]])
            setInviteRolesAvailable(inviteUserRoles.map((role) => InviteRolesToDisplayName[role]))
        }
    }, props.availableRoles)

    const handleSendInviteStudentClick = () => {
        let roles = []
        if (fixedRoles && fixedRoles.length > 0) {
            roles = fixedRoles
        } else {
            roles = [ReverseInviteRolesToDisplayName[inviteRoleDisplayName]]
        }

        if (inviteMode === INVITE_MODE_EMAIL) {
            let uniqueEmails = [...emails]
            if (leftoverChip.length > 0 && EmailValidator.validate(leftoverChip)) {
                uniqueEmails.push(leftoverChip)
            }
            uniqueEmails = [...new Set(uniqueEmails)]

            if (callBackFunctionOnAdd) {
                callBackFunctionOnAdd(organization.selectedOrganization, uniqueEmails, roles, userGroupId)
            } else {
                props.inviteStudentsToSchool(organization.selectedOrganization, uniqueEmails, roles, userGroupId)
            }
        } else {
            let uniquePhones = [...new Set(phoneNumbers)]
            props.inviteStudentsToSchoolWithPhone(
                organization.selectedOrganization,
                uniquePhones,
                roles,
                userGroupId,
                enableTwoFactor ? twoFactorProvider : null
            )
        }
        handleInviteEmailsClose()
    }

    const createChips = (newEmails) => {
        const { valid, invalid } = validateEmails(newEmails)
        const updatedValidEmails = [...emails, ...valid]
        setEmails(updatedValidEmails)

        if (invalid !== '') {
            const updatedRejectedEmails = [...rejectedEmails, ...invalid]
            setRejectedEmails(updatedRejectedEmails)
        }
    }

    const createPhoneChips = (input) => {
        const parts = input.replace(/[;,]/g, ' ').split(/\s+/).filter(Boolean)
        const valid = []
        const invalid = []
        parts.forEach((p) => {
            if (validatePhoneNumber(p)) {
                valid.push(normalizePhoneNumber(p))
            } else if (p !== '') {
                invalid.push(p)
            }
        })
        setPhoneNumbers([...phoneNumbers, ...valid])
        if (invalid.length > 0) {
            setRejectedPhones([...rejectedPhones, ...invalid])
        }
    }

    const handlePasteChips = (chips) => {
        chips.preventDefault()
        const text = chips.clipboardData.getData('Text')
        if (inviteMode === INVITE_MODE_EMAIL) {
            createChips(text)
        } else {
            createPhoneChips(text)
        }
    }

    const handleOnBeforeAdd = (chip) => {
        if (inviteMode === INVITE_MODE_EMAIL) {
            const chips = validateEmails(chip)
            return chips.valid !== ''
        } else {
            return validatePhoneNumber(chip)
        }
    }

    const validateEmails = (emails) => {
        emails = emails.replace(';', ' ')
        emails = emails.replace(',', ' ')
        emails = emails.split(/\s+/)

        let chips = []
        let rejectedEmails = []
        emails.forEach((email) => {
            EmailValidator.validate(email) ? chips.push(email) : email !== '' && rejectedEmails.push(email)
        })
        return {
            valid: chips,
            invalid: rejectedEmails,
        }
    }

    const handleOnDelete = (event, index) => {
        if (inviteMode === INVITE_MODE_EMAIL) {
            setEmails(emails.filter((_, i) => i !== index))
        } else {
            setPhoneNumbers(phoneNumbers.filter((_, i) => i !== index))
        }
    }

    const handleOnDeleteRejected = (index) => {
        if (inviteMode === INVITE_MODE_EMAIL) {
            setRejectedEmails(rejectedEmails.filter((_, i) => i !== index))
        } else {
            setRejectedPhones(rejectedPhones.filter((_, i) => i !== index))
        }
    }

    const handleInviteRoleChange = (event) => {
        setInviteRoleDisplayName([event.target.value])
    }

    const handleInviteModeChange = (event, newMode) => {
        if (newMode !== null) {
            setInviteMode(newMode)
        }
    }

    const currentItems = inviteMode === INVITE_MODE_EMAIL ? emails : phoneNumbers
    const currentRejected = inviteMode === INVITE_MODE_EMAIL ? rejectedEmails : rejectedPhones

    return (
        <Dialog open={inviteEmailsOpen} onClose={handleInviteEmailsClose} aria-labelledby="form-dialog-title" maxWidth="sm" fullWidth>
            <DialogTitle id="form-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{description}</DialogContentText>

                <ToggleButtonGroup
                    value={inviteMode}
                    exclusive
                    onChange={handleInviteModeChange}
                    className={classes.inviteModeToggle}
                    size="small">
                    <ToggleButton value={INVITE_MODE_EMAIL}>
                        <FormattedMessage id="dashboard.invite.mode.email" defaultMessage="Invite by Email" />
                    </ToggleButton>
                    <ToggleButton value={INVITE_MODE_PHONE}>
                        <FormattedMessage id="dashboard.invite.mode.phone" defaultMessage="Invite by Phone" />
                    </ToggleButton>
                </ToggleButtonGroup>

                {inviteRolesAvailable.length > 0 && (
                    <Grid onPaste={(chips) => handlePasteChips(chips)} item xs={12}>
                        {currentRejected.length > 0 && (
                            <div>
                                <div>
                                    <Typography component="p">
                                        <FormattedMessage
                                            id="dashboard.student.invite.rejected"
                                            defaultMessage="Invalid entries"
                                        />
                                    </Typography>
                                    {currentRejected.map((item, index) => (
                                        <Chip
                                            onDelete={() => handleOnDeleteRejected(index)}
                                            label={item}
                                            key={index}
                                            className={classes.chipRejected}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        <ChipInput
                            label={
                                inviteMode === INVITE_MODE_EMAIL ? (
                                    <FormattedMessage
                                        id="dashboard.student.typeorpaste"
                                        defaultMessage="Type or paste emails"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="dashboard.student.typeorpaste.phone"
                                        defaultMessage="Type or paste phone numbers (+47...)"
                                    />
                                )
                            }
                            className={classes.root}
                            value={currentItems}
                            onAdd={(chip) => {
                                if (inviteMode === INVITE_MODE_EMAIL) {
                                    createChips(chip)
                                } else {
                                    createPhoneChips(chip)
                                }
                            }}
                            onDelete={(chip, index) => handleOnDelete(chip, index)}
                            newChipKeyCodes={[13, 32, 188, 186]}
                            fullWidth={true}
                            onKeyUp={(chip) => setLeftoverChip(chip.target.value)}
                            onBeforeAdd={(chip) => handleOnBeforeAdd(chip)}
                            blurBehavior="add"
                            InputProps={{
                                autoFocus: true,
                            }}
                        />

                        {ENABLE_IDENTITY_VERIFICATION && inviteMode === INVITE_MODE_PHONE && (
                            <div style={{ marginTop: 16 }}>
                                <FormControl component="fieldset">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Checkbox
                                            checked={enableTwoFactor}
                                            onChange={(e) => setEnableTwoFactor(e.target.checked)}
                                            color="primary"
                                        />
                                        <Typography variant="body2">
                                            <FormattedMessage
                                                id="dashboard.invite.enable2fa"
                                                defaultMessage="Enable identity verification (2FA)"
                                            />
                                        </Typography>
                                    </div>
                                </FormControl>
                                {enableTwoFactor && (
                                    <FormControl fullWidth style={{ marginTop: 8 }}>
                                        <InputLabel>
                                            <FormattedMessage
                                                id="dashboard.invite.2fa.provider"
                                                defaultMessage="Verification provider"
                                            />
                                        </InputLabel>
                                        <Select
                                            value={twoFactorProvider}
                                            onChange={(e) => setTwoFactorProvider(e.target.value)}>
                                            <MenuItem value="Vipps">Vipps</MenuItem>
                                            <MenuItem value="BankID">BankID</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            </div>
                        )}

                        {(!fixedRoles || fixedRoles.length > 1) &&
                            (!inviteRolesAvailable || inviteRolesAvailable.length > 1) && (
                                <FormControl style={{ marginTop: 16 }}>
                                    <InputLabel htmlFor="age-simple">
                                        {<FormattedMessage id="dashboard.student.role" defaultMessage="Role" />}
                                    </InputLabel>
                                    <Select
                                        name="inviteRoles"
                                        value={inviteRoleDisplayName}
                                        onChange={handleInviteRoleChange}
                                        input={<Input id="select-multiple-checkbox" />}
                                        renderValue={(selected) => selected}>
                                        {inviteRolesAvailable.map((role) => (
                                            <MenuItem key={role} value={role}>
                                                <Checkbox checked={inviteRoleDisplayName.indexOf(role) > -1} />
                                                <ListItemText
                                                    style={{
                                                        color:
                                                            role === 'Capeesh Admin'
                                                                ? CapeeshColors.OrangeBright
                                                                : '#000000',
                                                    }}
                                                    primary={role}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleInviteEmailsClose} color="primary">
                    <FormattedMessage id="globalwords.cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                    onClick={handleSendInviteStudentClick}
                    variant={'contained'}
                    disabled={currentItems.length === 0}
                    color="primary">
                    <FormattedMessage id="globalwords.invite" defaultMessage="Invite" />
                </Button>
            </DialogActions>
        </Dialog>
    )
}

EmailInvite.propTypes = {}

const mapStateToProps = (state) => {
    return {
        organization: state.organization,
        metadata: state.metadata,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        inviteStudentsToSchool: (organizationId, uniqueEmails, inviteRoles, userGroupId) =>
            dispatch(
                organizationActions.inviteStudentsToSchool(organizationId, uniqueEmails, inviteRoles, userGroupId)
            ),
        inviteStudentsToSchoolWithPhone: (organizationId, phoneNumbers, inviteRoles, userGroupId, twoFactorProvider) =>
            dispatch(
                organizationActions.inviteStudentsToSchoolWithPhone(organizationId, phoneNumbers, inviteRoles, userGroupId, twoFactorProvider)
            ),
        enqueueSnackbar: (notification) => dispatch(snackbarActions.enqueueSnackbar(notification)),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(EmailInvite)
